import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import submit from "submit-form";
import { useRillContext } from "../../../rill/RillReact/share/base";
import type {
  ClientConfiguration as ClientConfigurationType,
  SectionKey,
} from "../../../types/clientConfig";
import { getInsightsLensLabel } from "../../../utils/insights-lens";
import { clientConfigRoleLabels } from "../../../views/athlete-portal/profile/components/ClientConfiguration/i18n";
import { SectionRadioGroups } from "../../../views/athlete-portal/profile/components/ClientConfiguration/SectionRadioGroups";
import { SectionToggleCard } from "../../../views/athlete-portal/profile/components/ClientConfiguration/SectionToggleCard";
import { TrackingItems } from "../../../views/athlete-portal/profile/components/ClientConfiguration/TrackingItems";
import { useVisibleSections } from "../../../views/athlete-portal/profile/components/ClientConfiguration/useVisibleSections";
import {
  type ClientConfigWidgetAvailability,
  DEFAULT_TEMPLATE_TYPES,
} from "../../../views/athlete-portal/profile/components/ClientConfiguration/utils";
import Select from "../../inputs/select";
import Modal from "../../modal-new";
import SvgIcon from "../../SvgIcon";
import ConfirmationModal from "../ConfirmationModal";
import { TemplateConflictModal } from "./TemplateConflictModal";

function ClientConfigurationManageTemplates() {
  const { t } = useTranslation(["athlete-portal", "common", "dashboard"]);
  const { locals } = useRillContext();
  const {
    clientConfigurationTemplates = [],
    activeUserToRequest,
    athlete,
    clientConfigSectionBlocks: sectionBlocks = {},
    clientConfigWidgetAvailability,
    activeUser,
  } = locals as {
    clientConfigurationTemplates?: ClientConfigurationType[];
    activeUserToRequest: { referAsClients?: boolean; _id?: string };
    athlete?: { _id: string };
    clientConfigSectionBlocks?: Partial<Record<SectionKey, unknown>>;
    clientConfigWidgetAvailability?: ClientConfigWidgetAvailability;
    activeUser?: { _id?: string };
  };
  const referAsClients = activeUserToRequest?.referAsClients || false;
  const roleLabels = clientConfigRoleLabels(t, referAsClients);

  const isCoachOwnedTemplate = (template: ClientConfigurationType) =>
    String(template.ownerId) === String(activeUser?._id);

  const assignableTemplates = useMemo(
    () =>
      clientConfigurationTemplates.filter(
        (template: ClientConfigurationType) =>
          !DEFAULT_TEMPLATE_TYPES.includes(template.templateType) &&
          !template.isForeignAssignment
      ),
    [clientConfigurationTemplates]
  );

  const [selectedTemplate, setSelectedTemplate] =
    useState<ClientConfigurationType | null>(null);
  const [mobileOptions, setMobileOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateIdToDelete, setTemplateIdToDelete] = useState<string | null>(
    null
  );
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictingSections, setConflictingSections] = useState<SectionKey[]>(
    []
  );

  useEffect(() => {
    if (assignableTemplates.length) {
      const options = assignableTemplates.map(
        (template: ClientConfigurationType) => ({
          value: template._id,
          label: template.templateName,
        })
      );
      setMobileOptions(options);
      setSelectedTemplate(assignableTemplates[0]);
    }
  }, [assignableTemplates]);

  const {
    isTrainingVisible,
    isGoalsVisible,
    isYourPlansVisible,
    isFemaleCycleEnabled,
    activeTrackerCount,
    isTrackingEnabled,
  } = useVisibleSections({ clientConfig: selectedTemplate || {} });

  const onAddNewTemplate = () => {
    submit("/actions/refresh", {
      method: "GET",
      body: {
        "modal[type]": "add-client-configuration-template",
      },
    });
  };

  const isSelectedDefault = !!selectedTemplate?.isDefault;

  const handleSetDefault = () => {
    submit("/actions/client-configuration/set-default-template", {
      method: "POST",
      body: { templateId: selectedTemplate._id },
    });
  };

  const handleUnsetDefault = () => {
    submit("/actions/client-configuration/unset-default-template", {
      method: "POST",
      body: { templateId: selectedTemplate._id },
    });
  };

  const handleAssignTemplate = () => {
    if (!athlete?._id || !selectedTemplate) return;

    const templateSections = selectedTemplate.sections ?? {};
    const conflicts = (Object.keys(templateSections) as SectionKey[]).filter(
      (section) =>
        templateSections[section]?.mode === "hidden" &&
        sectionBlocks[section] != null
    );

    if (conflicts.length > 0) {
      setConflictingSections(conflicts);
      setShowConflictModal(true);
      return;
    }

    submit(
      `/actions/client-configuration/reassign-template?athlete=${athlete._id}`,
      { method: "POST", body: { templateId: selectedTemplate._id } }
    );
  };

  const handleConfirmConflict = () => {
    if (!athlete?._id || !selectedTemplate) return;
    setShowConflictModal(false);
    submit(
      `/actions/client-configuration/reassign-template?athlete=${athlete._id}`,
      {
        method: "POST",
        body: {
          templateId: selectedTemplate._id,
          resolveConflicts: true,
          conflictingSections,
        },
      }
    );
  };

  const handleEdit = () => {
    if (!selectedTemplate) return;
    window.location.href = `/actions/refresh?modal[type]=add-client-configuration-template&currentConfigTemplateId=${selectedTemplate._id}`;
  };

  const openDeleteConfirm = (templateId: string) => {
    setTemplateIdToDelete(templateId);
    setDeleteConfirmOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setTemplateIdToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!templateIdToDelete) return;
    submit("/actions/client-configuration/delete-template", {
      method: "POST",
      body: { templateId: templateIdToDelete },
    });
    handleCancelDelete();
  };

  return (
    <>
      <Modal
        className={"manage-client-configuration-templates-modal"}
        title={t("clientConfiguration.manageTemplatesModal.title")}
        toClear={["currentConfigTemplateId"]}
      >
        <div className="body">
          {!assignableTemplates.length ? (
            <section className="no-questions">
              <div>
                <h3>
                  {t("clientConfiguration.manageTemplatesModal.emptyTitle")}
                </h3>
                <p className="p2">
                  <Trans
                    t={t}
                    i18nKey="clientConfiguration.manageTemplatesModal.emptyDescription"
                    values={roleLabels}
                    components={{ 1: <strong /> }}
                  />
                </p>
                <button
                  onClick={onAddNewTemplate}
                  type="button"
                  data-button-type="primary"
                >
                  {t("clientConfiguration.manageTemplatesModal.addNew")}
                </button>
              </div>
            </section>
          ) : (
            <>
              <aside className="force-mobile-hidden">
                <p className="p5">
                  {t("clientConfiguration.manageTemplatesModal.selectLabel")}
                </p>
                <ul>
                  {assignableTemplates.map(
                    (item: ClientConfigurationType, i: number) => (
                      <li
                        key={`${item.templateName}-${i}-column`}
                        aria-current={
                          selectedTemplate?._id === item._id ? true : undefined
                        }
                        onClick={() => setSelectedTemplate(item)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ")
                            setSelectedTemplate(item);
                        }}
                      >
                        <p className="p1">
                          {item.templateName}
                          {item.templateType === "global" && (
                            <span className="p3">
                              {t(
                                "clientConfiguration.assignTemplateModal.globalSuffix"
                              )}
                            </span>
                          )}
                        </p>
                        {selectedTemplate?._id === item._id &&
                          isCoachOwnedTemplate(item) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteConfirm(item._id);
                              }}
                              className="config-template-delete-button"
                            >
                              <img
                                src="/img/new-icons/red-trash.svg"
                                alt={t(
                                  "clientConfiguration.manageTemplatesModal.deleteAlt"
                                )}
                              />
                            </button>
                          )}
                      </li>
                    )
                  )}
                </ul>
              </aside>
              <section className="mobile-templates-list only-show-mobile">
                <p className="p5">
                  {t("clientConfiguration.manageTemplatesModal.selectLabel")}
                </p>
                <div className="select">
                  <Select
                    name="client-config-template-select"
                    disabled={false}
                    options={mobileOptions}
                    onChange={(value: string) => {
                      const template = assignableTemplates.find(
                        (template: ClientConfigurationType) =>
                          template._id === value
                      );
                      setSelectedTemplate(template);
                    }}
                    value={selectedTemplate?.templateName}
                  />
                </div>
              </section>
              {selectedTemplate && (
                <section
                  className="athlete-profile client-configuration"
                  aria-label={t(
                    "clientConfiguration.manageTemplatesModal.previewAriaLabel"
                  )}
                >
                  <header>
                    <h5>{String(selectedTemplate.templateName)}</h5>
                    <div>
                      {isCoachOwnedTemplate(selectedTemplate) && (
                        <button
                          type="button"
                          onClick={() =>
                            openDeleteConfirm(selectedTemplate._id)
                          }
                          data-button-type="text-white"
                          data-icon-only
                        >
                          <img
                            src="/img/new-icons/red-trash.svg"
                            alt="delete"
                          />
                        </button>
                      )}
                      {isCoachOwnedTemplate(selectedTemplate) && (
                        <>
                          <button
                            type="button"
                            className="force-mobile-hidden"
                            data-button-type="tertiary"
                            onClick={handleEdit}
                          >
                            {t(
                              "clientConfiguration.manageTemplatesModal.editTemplate"
                            )}
                          </button>
                          <button
                            type="button"
                            className="only-show-mobile"
                            data-button-type="tertiary"
                            onClick={handleEdit}
                          >
                            <img
                              src="/img/new-icons/edit-pencil.svg"
                              alt={t(
                                "clientConfiguration.manageTemplatesModal.editAlt"
                              )}
                            />
                          </button>
                        </>
                      )}
                    </div>
                  </header>
                  <section
                    className="client-config-sections"
                    id="client-config-modal-templates-body"
                  >
                    <SectionToggleCard
                      variant="readonly"
                      icon="🏋"
                      titleKey="clientConfiguration.sections.training"
                      hiddenHintKey="clientConfiguration.copy.training.hiddenHint"
                      visibleHintKey="clientConfiguration.copy.training.activeHint"
                      hiddenHintClassName="chip warning"
                      roleLabels={roleLabels}
                      visible={isTrainingVisible}
                    />

                    <SectionRadioGroups
                      roleLabels={roleLabels}
                      sectionWidgetAvailability={clientConfigWidgetAvailability}
                      getSectionMode={(key) =>
                        selectedTemplate.sections?.[key]?.mode
                      }
                      variant="readonly"
                      showSectionIcons={false}
                    />

                    <SectionToggleCard
                      variant="readonly"
                      icon="🎯"
                      titleKey="clientConfiguration.sections.goals"
                      hiddenHintKey="clientConfiguration.copy.goals.hiddenHint"
                      visibleHintKey="clientConfiguration.copy.goals.visibleHint"
                      roleLabels={roleLabels}
                      visible={isGoalsVisible}
                    />

                    <div className="client-config-card tracking">
                      <div className="client-config-card__header">
                        <div className="client-config-card__left">
                          <div className="client-config-card__title">
                            <span>
                              📊{" "}
                              {t(
                                "clientConfiguration.sections.trackingRequirements"
                              )}
                            </span>
                            {isTrackingEnabled && (
                              <span className="chip info">
                                {t("clientConfiguration.activeTrackers", {
                                  count: activeTrackerCount,
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="client-config-card__right">
                          <span className="chip info">
                            {isTrackingEnabled
                              ? t("clientConfiguration.chips.on")
                              : t("clientConfiguration.chips.hidden")}
                          </span>
                        </div>
                      </div>
                      {isTrackingEnabled && (
                        <TrackingItems
                          roleLabels={roleLabels}
                          selectedTemplate={selectedTemplate}
                          readOnly={true}
                        />
                      )}
                    </div>

                    <SectionToggleCard
                      variant="readonly"
                      icon="🩸"
                      titleKey="clientConfiguration.sections.femaleCycle"
                      hiddenHintKey="clientConfiguration.copy.femaleCycleTracking.hiddenHint"
                      visibleHintKey="clientConfiguration.copy.femaleCycleTracking.visibleHint"
                      roleLabels={roleLabels}
                      visible={isFemaleCycleEnabled}
                    />

                    <SectionToggleCard
                      variant="readonly"
                      icon="📅"
                      titleKey="clientConfiguration.sections.yourPlans"
                      hiddenHintKey="clientConfiguration.copy.yourPlans.hiddenHint"
                      visibleHintKey="clientConfiguration.copy.yourPlans.visibleHint"
                      roleLabels={roleLabels}
                      visible={isYourPlansVisible}
                    />

                    <div className="client-config-card">
                      <div className="client-config-card__left">
                        <div className="client-config-card__title">
                          <span>
                            📈 {t("clientConfiguration.templateLens.label")}
                          </span>
                        </div>
                      </div>
                      <div className="client-config-card__right">
                        <span className="chip info">
                          {selectedTemplate.defaultInsightsLens
                            ? getInsightsLensLabel(
                                t,
                                selectedTemplate.defaultInsightsLens
                              )
                            : t("clientConfiguration.templateLens.notDefined")}
                        </span>
                      </div>
                    </div>
                  </section>
                </section>
              )}
            </>
          )}
        </div>
        {selectedTemplate && (
          <>
            {/* footer for desktop */}
            <footer className="acc-template-footer client-config-footer-buttons force-mobile-hidden">
              <aside>
                <button
                  onClick={onAddNewTemplate}
                  type="button"
                  data-button-type="outline"
                >
                  {t("clientConfiguration.manageTemplatesModal.addNew")}
                </button>

                <button
                  type="button"
                  data-button-type="outline"
                  onClick={
                    isSelectedDefault ? handleUnsetDefault : handleSetDefault
                  }
                >
                  <SvgIcon
                    src={
                      isSelectedDefault
                        ? "/img/new-icons/checked-square.svg"
                        : "/img/new-icons/unchecked-square.svg"
                    }
                  />
                  <p className="p4">
                    {isSelectedDefault
                      ? t("clientConfiguration.manageTemplatesModal.default")
                      : t(
                          "clientConfiguration.manageTemplatesModal.setAsDefault"
                        )}
                  </p>
                </button>
              </aside>
              {!!athlete?._id && (
                <button
                  type="button"
                  onClick={handleAssignTemplate}
                  data-button-type="primary"
                >
                  {t(
                    "clientConfiguration.manageTemplatesModal.assignToRole",
                    roleLabels
                  )}
                </button>
              )}
            </footer>
            {/* footer for mobile */}
            <footer className="client-config-template-footer client-config-footer-buttons force-only-show-mobile">
              <div className="client-config-template-footer-wrapper">
                <button
                  type="button"
                  onClick={
                    isSelectedDefault ? handleUnsetDefault : handleSetDefault
                  }
                >
                  <SvgIcon
                    src={
                      isSelectedDefault
                        ? "/img/new-icons/checked-square.svg"
                        : "/img/new-icons/unchecked-square.svg"
                    }
                  />
                  <p className="p4">
                    {isSelectedDefault
                      ? t("clientConfiguration.manageTemplatesModal.default")
                      : t(
                          "clientConfiguration.manageTemplatesModal.setAsDefault"
                        )}
                  </p>
                </button>
                <div>
                  <button
                    onClick={onAddNewTemplate}
                    type="button"
                    data-button-type="outline"
                  >
                    {t("clientConfiguration.manageTemplatesModal.addNew")}
                  </button>
                  {!!athlete?._id && (
                    <button
                      type="button"
                      onClick={handleAssignTemplate}
                      data-button-type="primary"
                    >
                      {t(
                        "clientConfiguration.manageTemplatesModal.assignToRole",
                        roleLabels
                      )}
                    </button>
                  )}
                </div>
              </div>
            </footer>
          </>
        )}
      </Modal>
      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        title={t("clientConfiguration.manageTemplatesModal.deleteTitle")}
        contentText={t(
          "clientConfiguration.manageTemplatesModal.deleteContent"
        )}
        cancelButtonText={t("common:cancel")}
        cancelButtonAction={handleCancelDelete}
        confirmButtonText={t("clientConfiguration.manageTemplatesModal.remove")}
        confirmButtonAction={handleConfirmDelete}
        icon="delete"
      />
      <TemplateConflictModal
        isVisible={showConflictModal}
        conflictingSections={conflictingSections}
        onConfirm={handleConfirmConflict}
        onCancel={() => setShowConflictModal(false)}
      />
    </>
  );
}

export default ClientConfigurationManageTemplates;
